export const BIO_SEARCH_ALGORITHM_TYPES = {
    Blast: 'Blast',
    Nw: 'Nw',
} as const;

export const GLOBAL_ALIGNMENT_PERMISSION_TODO =
    '[TODO][Bio Global Alignment] 当前用户无 globalAlignment 权限，已将历史 Nw 设置回退为 Blast；未来接入升级能力后调整。';

type SubjectLength = {
    start?: number;
    end?: number;
};

type SearchSetting = {
    [key: string]: unknown;
    algorithm_type?: string;
    sequenceType?: string;
    dbType?: string;
    gap_open?: number;
    gap_extend?: number;
    score_matrix?: string;
    matrix?: string;
    gaps?: string;
    match_score?: number;
    mis_match_score?: number;
    match_scores?: string;
    ungapped?: boolean;
    perc_identity?: unknown;
    limit?: number;
    subject_length?: SubjectLength;
};

const parsePair = (value: unknown) => {
    if (typeof value !== 'string') return [];
    return value.split(',').map(item => Number(item));
};

const normalizeSubjectLength = (subjectLength: SubjectLength = {}) => ({
    start: subjectLength.start ?? 0,
    ...(subjectLength.end ? { end: subjectLength.end } : {}),
});

export const isNwSearchSetting = (setting: SearchSetting = {}) =>
    setting.algorithm_type === BIO_SEARCH_ALGORITHM_TYPES.Nw;

export const normalizeManualSearchSetting = (
    setting: SearchSetting = {},
    hasGlobalAlignment: boolean,
    warn: (message: string) => void = message => {
        // eslint-disable-next-line no-console
        console.warn(message);
    },
) => {
    const normalized = {
        ...setting,
        algorithm_type: setting.algorithm_type || BIO_SEARCH_ALGORITHM_TYPES.Blast,
    };

    if (!hasGlobalAlignment && isNwSearchSetting(normalized)) {
        warn(GLOBAL_ALIGNMENT_PERMISSION_TODO);
        normalized.algorithm_type = BIO_SEARCH_ALGORITHM_TYPES.Blast;
    }

    return normalized;
};

export const restoreNwSearchSetting = (setting: SearchSetting = {}) => {
    if (!isNwSearchSetting(setting)) return setting;

    const restored = {
        ...setting,
        score_matrix: setting.score_matrix || setting.matrix,
    };
    const [gapOpen, gapExtend] = parsePair(setting.gaps);
    const [matchScore, mismatchScore] = parsePair(setting.match_scores);

    if (Number.isFinite(gapOpen) && Number.isFinite(gapExtend)) {
        restored.gap_open = gapOpen;
        restored.gap_extend = gapExtend;
    }
    if (Number.isFinite(matchScore) && Number.isFinite(mismatchScore)) {
        restored.match_score = matchScore;
        restored.mis_match_score = mismatchScore;
    }

    return restored;
};

export const generateNwSetting = (setting: SearchSetting = {}) => ({
    algorithm_type: BIO_SEARCH_ALGORITHM_TYPES.Nw,
    ungapped: setting.ungapped,
    gaps: `${setting.gap_open},${setting.gap_extend}`,
    matrix: setting.score_matrix,
    ...(setting.sequenceType === 'NUCLEOTIDE'
        ? { match_scores: `${setting.match_score ?? 1},${setting.mis_match_score ?? 0}` }
        : {}),
    perc_identity: setting.perc_identity,
    limit: setting.limit,
    subject_length: normalizeSubjectLength(setting.subject_length),
});

export const isNwCrossDatabase = (setting: SearchSetting = {}) => {
    if (!isNwSearchSetting(setting)) return false;
    if (setting.dbType === 'ALL') return true;

    return Boolean(setting.sequenceType && setting.dbType && setting.sequenceType !== setting.dbType);
};
